/**
 * Money formatting. One place, so the currency standard is changed here and nowhere else.
 *
 * The store trades in Albanian Lek, ISO 4217 code **ALL**. Every amount shown to a user goes
 * through `formatLek` — no component builds a price string by concatenating a number and a
 * symbol, because that is how "1500L" and "1 500 L" end up on the same page.
 *
 * Formatting is locale-driven via Intl with `currencyDisplay: "code"`, which yields "350 ALL"
 * and "1 234 567 ALL". Note the sq-AL locale only groups from five digits up, so 1500 renders
 * as "1500 ALL" — that is correct Albanian convention, not a missing separator.
 *
 * Lek has no circulating minor unit, so amounts are shown as whole numbers.
 */

/** ISO 4217 code for the store's currency. */
export const CURRENCY_CODE = "ALL";

const DISPLAY_LOCALE = "sq-AL";

const displayFormatter = new Intl.NumberFormat(DISPLAY_LOCALE, {
  style: "currency",
  currency: CURRENCY_CODE,
  currencyDisplay: "code",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const groupedFormatter = new Intl.NumberFormat(DISPLAY_LOCALE, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format a value as an amount in ALL.
 *
 * @param {number|string|null|undefined} value
 * @returns {string} e.g. "350 ALL", "1 234 567 ALL"
 */
export function formatLek(value) {
  const n = Number(value);
  if (!isFinite(n)) return displayFormatter.format(0);
  return displayFormatter.format(n);
}

/** Preferred name going forward; `formatLek` is kept because it is used across the app. */
export const formatCurrency = formatLek;

/**
 * Group the digits of a number without attaching the currency code — for use *inside* an input
 * that already shows "ALL" as a suffix, where repeating the code would read as "1 500 ALL ALL".
 *
 * @param {number|string|null|undefined} value
 * @returns {string} e.g. "1 234 567", or "" for a value that isn't a number
 */
export function formatAmount(value) {
  const n = Number(value);
  if (value === "" || value === null || value === undefined || !isFinite(n)) return "";
  return groupedFormatter.format(n);
}

/**
 * Read a number back out of whatever a user typed: grouped digits, a stray currency code, a
 * comma used as the decimal mark (the Albanian convention), spaces of any width.
 *
 * @param {string|number|null|undefined} input
 * @returns {number|null} the parsed amount, or null if there is no number in there
 */
export function parseAmount(input) {
  if (typeof input === "number") return isFinite(input) ? input : null;
  if (!input) return null;

  const cleaned = String(input)
    // Every space ICU might emit as a group separator, including NBSP and narrow NBSP.
    .replace(/[\s  ]/g, "")
    .replace(/ALL/gi, "")
    .replace(/Lekë|Lek|L/gi, "")
    .replace(",", ".")
    // Keep the sign: stripping it turned a typed "-5" into 5, so a negative price sailed
    // straight past validation and got published as a positive one.
    .replace(/[^\d.-]/g, "");

  // A sign only means anything at the front of the number.
  const normalised = cleaned.replace(/(?!^)-/g, "");
  if (!/\d/.test(normalised)) return null;

  const n = Number(normalised);
  return isFinite(n) ? n : null;
}
